'use client';

import { deleteCookie, getCookie, setCookie } from 'cookies-next';

import { clientAxios } from '../api';
import { PATH } from '../constants';
import {
  deleteAuthCookiesAtServer,
  getCookieAtServer,
} from '../utils/handleCookie';

import { LogLevel, logger } from '../utils';

import { decodeJwt } from './jwt';
import Auth from './Auth';

const A_WEEK = 60 * 60 * 24 * 7;

class ClientAuth {
  private static inflightPromise: Promise<string | null> | null = null;

  private static async _setLegacyInterfaceWART(wSessionId: string) {
    try {
      const { data } = await clientAxios.post(
        `${PATH.WISHKET_OIDC_LEGACY_INTERFACE_URL}?wishketOidcOrigin=${encodeURIComponent(process.env.NEXT_PUBLIC_APP_BASE_URL || '')}`,
        {
          w_session_id: wSessionId,
        }
      );

      const newWART = data.data.WART;

      if (!data.ok) {
        return null;
      }

      if (newWART) {
        setCookie('WART', newWART, {
          path: '/',
          maxAge: A_WEEK,
          domain: Auth.getDomain(),
        });

        return newWART;
      } else {
        return null;
      }
    } catch (e) {
      return this._failToGetAccessToken();
    }
  }

  static async getAccessToken() {
    if (this.inflightPromise) return this.inflightPromise;

    this.inflightPromise = this._getAccessToken();
    return this.inflightPromise;
  }

  private static async _getAccessToken() {
    let WART = getCookie('WART');
    const hasWART = !!WART;
    const wSessionId = await getCookieAtServer('wsessionid');
    const hasWSessionId = !!wSessionId;

    if (!hasWSessionId) return this._failToGetAccessToken();

    if (!hasWART && hasWSessionId) {
      WART = await ClientAuth._setLegacyInterfaceWART(wSessionId);
    }

    if (!WART) return null;

    try {
      const { data } = await clientAxios.get(PATH.WISHKET_OIDC_URL, {
        withCredentials: true,
      });

      if (!data.ok || !data.data || !data.data.access_token) {
        throw new Error('Failed to get access token in ClientAuth');
      }

      return data.data.access_token;
    } catch (e) {
      const isRefreshed = await Auth.refreshAccessToken();
      if (!isRefreshed) await ClientAuth.deAuthenticate();
    }
  }

  private static _failToGetAccessToken = async () => {
    await ClientAuth.deAuthenticate();

    return null;
  };

  static async deAuthenticate() {
    await ClientAuth.deleteCookies();
  }

  static async deleteCookies() {
    deleteCookie('WART', {
      domain: Auth.getDomain(),
      path: '/',
    });

    await deleteAuthCookiesAtServer();
  }

  static async getProfile() {
    const accessToken = await ClientAuth.getAccessToken();
    if (!accessToken) return null;

    const payload = decodeJwt(accessToken);

    if (!payload) return null;

    const {
      user_id,
      username,
      email,
      yozmit_onboarding_status,
      yozmit_profile_id,
      is_staff,
    } = payload;

    return {
      userId: user_id,
      username,
      email,
      yozmitOnboardingStatus: yozmit_onboarding_status,
      yozmitProfileId: yozmit_profile_id,
      isStaff: is_staff,
    };
  }

  static async getIsActivated(): Promise<boolean | null> {
    const WART = getCookie('WART');
    const hasWART = !!WART;

    if (!hasWART) return null;

    const accessToken = await ClientAuth.getAccessToken();
    if (!accessToken) return null;

    try {
      const { data } = await clientAxios.get(
        `${PATH.WISHKET_OIDC_ACTIVATION_URL}?wishketOidcOrigin=${encodeURIComponent(process.env.NEXT_PUBLIC_APP_BASE_URL || '')}`,
        {
          withCredentials: true,
        }
      );

      if (!data.ok) return null;
      if (!data.data) return null;

      return data.data.is_activated;
    } catch (e) {
      const isRefreshed = await Auth.refreshAccessToken();
      if (!isRefreshed) await ClientAuth.deAuthenticate();

      return null;
    }
  }

  static async sendActivationEmail(email: string) {
    if (!email) return null;

    try {
      const { data } = await clientAxios.post(
        `${process.env.NEXT_PUBLIC_APP_AUTH_URL}/api/resendEmailVerifyApi?wishketOidcOrigin=${encodeURIComponent(process.env.NEXT_PUBLIC_APP_BASE_URL || '')}`,
        {
          email,
        },
        {
          withCredentials: true,
        }
      );

      if (!data.ok) throw new Error('Failed to send activation email');
    } catch (error) {
      logger(LogLevel.ERROR, error, {
        message: 'sendActivationEmail error',
        detail: {
          email,
        },
      });

      throw error;
    }
  }
}

export default ClientAuth;
